import { Outlet } from '@tanstack/react-router';
import { useOrdersListEventListener } from '../../hooks/use-orders-list-event-listener';
import { useStoreEventListener } from '../../hooks/use-store-event-listener';
import { storeLayout } from '../../route-apis';
import { StoreWebSocketProvider } from '../../websocket/store-websocket-provider';
import { DisabledStore } from '../disabled-store';
import { ProductEditModalPortal } from '../product-table/product-edit-modal-portal';

function StoreLayoutInner({ storeId }: { storeId: string }) {
  useStoreEventListener(storeId);
  useOrdersListEventListener(storeId);

  return (
    <>
      <Outlet />
      <ProductEditModalPortal />
    </>
  );
}

// Component for the /stores/$storeId route.
export function StoreLayoutComponent() {
  const { storeId } = storeLayout.useParams();
  const { store } = storeLayout.useRouteContext();

  // Route context is NOT guaranteed populated on every render — the same
  // in-flight-beforeLoad window useCurrentStore guards. When a navigation
  // inside this layout is superseded by a second one, the router republishes
  // this match with status:"success" but an empty context, because lane matches
  // are shallow copies that inherit the previous status while beforeLoad's
  // result is cleared and re-merged around an await. defaultPendingMs: 0 makes
  // that publication eager, so the layout really does render store-less for a
  // frame before beforeLoad refills it.
  //
  // Gate the whole subtree here rather than guarding the takeover below: this
  // layout is the context boundary, and its descendants (useProducts, useStore,
  // every page reading `store` off this route) are typed against beforeLoad's
  // return, so they dereference it unconditionally. Rendering <Outlet/> with an
  // empty context only moves the crash one level down. Render nothing for that
  // frame instead of a spinner — a pending flash is exactly the flicker
  // DelegatingPending (delegating-pending.tsx) exists to avoid.
  if (!store) {
    return null;
  }

  // One takeover for every store route. It also kills the WebSocket $connect,
  // which authorizeStoreAccess would refuse anyway. beforeLoad has already
  // redirected disabled-without-subscription stores to onboarding, so reaching
  // this branch means a lapsed payment — exactly what <DisabledStore/> describes.
  if (!store.enabled) {
    return <DisabledStore />;
  }

  return (
    <StoreWebSocketProvider storeId={storeId}>
      <StoreLayoutInner storeId={storeId} />
    </StoreWebSocketProvider>
  );
}
